import {
  Alert,
  Box,
  Button,
  Card,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type FormEvent, useState } from "react";
import { loginUser } from "../api/auth";
import { useNotifications } from "../components/NotificationProvider";

type LoginPageProps = {
  onLogin: (user: {
    id: number;
    name: string;
    email: string;
    token: string;
    role: "admin" | "manager" | "inspector" | "file_viewer";
  }) => void;
  onSwitchToTenant: () => void;
};

export function LoginPage({ onLogin, onSwitchToTenant }: LoginPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showAlert, showSnackbar } = useNotifications();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError("Please enter a valid email address");
      return;
    }
    if (!password || password.length < 1) {
      setError("Password is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await loginUser(trimmedEmail, password);
      onLogin({
        id: response.user.id,
        name: response.user.name || response.user.email,
        email: response.user.email,
        token: response.token,
        role: response.user.role,
      });
      showSnackbar({
        title: "Signed in",
        message: "Welcome back to HMO Manager.",
        variant: "success",
      });
    } catch (err) {
      let message = "Unable to sign in";
      if (err instanceof Error) {
        try {
          const parsed = JSON.parse(err.message);
          if (parsed && parsed.error) {
            message = parsed.error;
          } else {
            message = err.message;
          }
        } catch {
          const m = err.message || "";
          if (m.includes("Failed to fetch") || m.includes("NetworkError")) {
            message = "Cannot reach server";
          } else {
            message = m;
          }
        }
      }

      setError(message);
      showAlert({ title: "Sign-in failed", message, variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className="auth-page">
      <Card
        sx={{
          width: "min(100%, 480px)",
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(15, 23, 42, 0.12)",
          border: "1px solid rgba(15, 23, 42, 0.06)",
        }}
      >
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            background: "linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)",
            color: "white",
          }}
        >
          <Typography
            variant="overline"
            sx={{ letterSpacing: 1.4, opacity: 0.9 }}
          >
            Secure access
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
            Welcome back
          </Typography>
          <Typography sx={{ mt: 1, color: "rgba(255,255,255,0.82)" }}>
            Sign in with your authorized workspace credentials to manage your
            HMO operations.
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 3, md: 4 } }}>
          <Stack spacing={2.5}>
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2}>
                <TextField
                  label="Work email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@company.com"
                  required
                  fullWidth
                  autoComplete="email"
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Enter your password"
                  required
                  fullWidth
                  autoComplete="current-password"
                />
                {error ? <Alert severity="error">{error}</Alert> : null}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isSubmitting}
                  sx={{
                    py: 1.3,
                    borderRadius: 2,
                    fontWeight: 600,
                    boxShadow: "none",
                    "&:hover": { boxShadow: "none" },
                  }}
                >
                  {isSubmitting ? "Signing in..." : "Sign in securely"}
                </Button>
              </Stack>
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Protected authentication
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Your credentials are handled securely and are never displayed in
                this page.
              </Typography>
            </Box>

            <Button
              variant="text"
              onClick={onSwitchToTenant}
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              Sign in as tenant instead
            </Button>
          </Stack>
        </Box>
      </Card>
    </Box>
  );
}
